import Phaser from "phaser";
import { ICON_ART_SIZE, type IconPlace } from "odyssey-shared";
import type { IconAtlasState } from "./art.ts";

/**
 * The page's icon atlas as a canvas texture (ADR-111): the decoded atlas the page loaded once (`page-icons.ts`) handed
 * to the texture manager under one key, filtered nearest, and a frame added for an icon key the first time a surface
 * draws it. Every canvas surface that draws icon art, the emoji over characters and the ground items, shares the one
 * texture, so the atlas is uploaded once.
 */

/**
 * The texture key the page's icon atlas is added under.
 */
export const ICON_TEXTURE = "icons";

/**
 * The texture key the same atlas is added under, filtered linear, for art drawn downscaled off the whole device pixels
 * (ADR-112 decision 4.3), which nearest filtering breaks into ragged marks.
 */
export const ICON_SMOOTH_TEXTURE = "icons-smooth";

/**
 * The atlas's texture, added once the atlas has decoded, filtered nearest.
 *
 * @param textures The scene's texture manager.
 * @param atlas The page's icon atlas as it stands.
 * @returns The texture, or null while the atlas loads, after it failed, or when the page did not decode it itself.
 */
export function iconTexture(textures: Phaser.Textures.TextureManager, atlas: IconAtlasState): Phaser.Textures.Texture | null {
  if (atlas.status !== "ready" || atlas.image.element === undefined) {
    return null;
  }
  const texture = textures.exists(ICON_TEXTURE) ? textures.get(ICON_TEXTURE) : textures.addImage(ICON_TEXTURE, atlas.image.element);
  texture?.setFilter(Phaser.Textures.FilterMode.NEAREST);
  return texture;
}

/**
 * The frame an icon key draws in the atlas's texture, added the first time it is asked for.
 *
 * @param texture The atlas's texture.
 * @param key The icon key, which names the frame.
 * @param place Where the key's art stands in the atlas, asked only when the texture lacks the frame; null when the
 *   pointer lacks the key.
 * @returns The frame's name, the key, or null when the pointer lacks it.
 */
export function iconFrame(texture: Phaser.Textures.Texture, key: string, place: () => IconPlace | null): string | null {
  if (texture.has(key)) {
    return key;
  }
  const at = place();
  if (at === null) {
    return null;
  }
  texture.add(key, 0, at.x, at.y, ICON_ART_SIZE, ICON_ART_SIZE);
  return key;
}

/**
 * The atlas's second texture, the same decoded image filtered linear, added the first time art is drawn downscaled.
 *
 * @param textures The scene's texture manager.
 * @param atlas The page's icon atlas as it stands.
 * @returns The texture, or null while the atlas loads, after it failed, or when the page did not decode it itself.
 */
export function iconSmoothTexture(textures: Phaser.Textures.TextureManager, atlas: IconAtlasState): Phaser.Textures.Texture | null {
  if (atlas.status !== "ready" || atlas.image.element === undefined) {
    return null;
  }
  const texture = textures.exists(ICON_SMOOTH_TEXTURE) ? textures.get(ICON_SMOOTH_TEXTURE) : textures.addImage(ICON_SMOOTH_TEXTURE, atlas.image.element);
  texture?.setFilter(Phaser.Textures.FilterMode.LINEAR);
  return texture;
}
